import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  Item,
  Paging,
  SearchPanel,
  Toolbar,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { Label, RequiredRule, SimpleItem } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import axios, { AxiosError } from "axios";

import GridContainer from "../Productos/GridContainer";
import * as S from "./Linea.styles";
import { apiService } from "../../../services/apiService";
import { ApiError } from "../../../services/apiError";
import { Sucursal } from "../../../dtos/Sucursal";
import {
  CatLinea,
  LineaGridRow,
  ManifoldOption,
} from "../../../dtos/CatLinea";
import {
  getSessionSucursalId,
  isManifoldCatalogReadOnly,
} from "../../../helpers/session";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError & { details?: string }>;
    const data = error.response?.data as
      | { message?: string; details?: string }
      | undefined;
    return {
      message:
        data?.message ||
        data?.details ||
        "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const isActivoEstatus = (estatus?: string | null) =>
  !estatus || estatus.trim().toLowerCase() === "activo";

const pick = <T,>(
  obj: Record<string, unknown>,
  keys: string[],
): T | undefined => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return obj[key] as T;
    }
  }
  return undefined;
};

const normalizeLinea = (raw: unknown): CatLinea => {
  const item = (raw ?? {}) as Record<string, unknown>;
  return {
    id_Linea: Number(pick(item, ["id_Linea", "Id_Linea", "idLinea"]) ?? 0),
    nombreLinea: String(pick(item, ["nombreLinea", "NombreLinea"]) ?? ""),
    id_Sucursal: Number(
      pick(item, ["id_Sucursal", "iD_Sucursal", "ID_Sucursal", "idSucursal"]) ??
        0,
    ),
    id_Manifold: (() => {
      const value = pick(item, ["id_Manifold", "Id_Manifold", "idManifold"]);
      return value == null ? null : Number(value);
    })(),
    estatus: String(pick(item, ["estatus", "Estatus"]) ?? "Activo"),
    fechaRegistro: pick<string>(item, ["fechaRegistro", "FechaRegistro"]),
    fechaEdicion: pick<string | null>(item, ["fechaEdicion", "FechaEdicion"]),
  };
};

const normalizeManifoldOption = (raw: unknown): ManifoldOption | null => {
  const item = (raw ?? {}) as Record<string, unknown>;

  // Configuración Manifold vive en Registro_Manifold (Id_Registro + Nombre).
  const idRegistro = Number(
    pick(item, ["id_Registro", "Id_Registro", "idRegistro"]) ?? 0,
  );
  const idManifoldLegacy = Number(
    pick(item, ["id_Manifold", "Id_Manifold", "idManifold"]) ?? 0,
  );
  const idManifold = idRegistro || idManifoldLegacy;
  if (!idManifold) return null;

  const registro = (pick<Record<string, unknown>>(item, [
    "registro",
    "Registro",
  ]) ?? {}) as Record<string, unknown>;

  const nombre =
    String(pick(item, ["nombre", "Nombre"]) ?? "").trim() ||
    String(pick(registro, ["nombre", "Nombre"]) ?? "").trim() ||
    String(pick(item, ["nombre_Manifold", "Nombre_Manifold"]) ?? "").trim() ||
    `Manifold #${idManifold}`;

  const idSucursal = Number(
    pick(item, ["id_Sucursal", "iD_Sucursal", "ID_Sucursal", "idSucursal"]) ??
      pick(registro, [
        "id_Sucursal",
        "iD_Sucursal",
        "ID_Sucursal",
        "idSucursal",
      ]) ??
      0,
  );

  const sucursal = (pick<Record<string, unknown>>(item, [
    "sucursal",
    "Sucursal",
  ]) ??
    pick<Record<string, unknown>>(registro, ["sucursal", "Sucursal"]) ??
    {}) as Record<string, unknown>;

  const estatus = String(
    pick(item, ["estatus", "Estatus"]) ??
      pick(registro, ["estatus", "Estatus"]) ??
      "Activo",
  );

  return {
    id_Manifold: idManifold,
    nombre,
    id_Sucursal: idSucursal,
    numSucursal: pick(sucursal, ["numSucursal", "NumSucursal"]),
    nombreSucursal: pick<string>(sucursal, ["nombreSuc", "NombreSuc"]),
    estatus,
  };
};

const toGridRow = (
  linea: CatLinea,
  sucursalesById: Map<number, Sucursal>,
  manifoldsById: Map<number, ManifoldOption>,
): LineaGridRow => {
  const activo = isActivoEstatus(linea.estatus);
  const sucursal = sucursalesById.get(linea.id_Sucursal);
  const manifold = linea.id_Manifold
    ? manifoldsById.get(linea.id_Manifold)
    : undefined;

  return {
    id_Linea: linea.id_Linea,
    nombreLinea: linea.nombreLinea,
    id_Sucursal: linea.id_Sucursal,
    numCodSucursal: sucursal?.numSucursal ?? "—",
    nombreSucursal: sucursal?.nombreSuc || "—",
    id_Manifold: linea.id_Manifold,
    nombreManifold: manifold?.nombre || (linea.id_Manifold ? `#${linea.id_Manifold}` : "—"),
    estatus: activo ? "Activo" : "Inactivo",
    activo,
  };
};

interface SucursalOption extends Sucursal {
  idNum: number;
  searchText: string;
}

interface FormState {
  id_Linea?: number;
  nombreLinea: string;
  id_Manifold: number | null;
  id_Sucursal: number | null;
}

const LineaIndex: React.FC = () => {
  const navigate = useNavigate();
  const dataGridRef = useRef<any>(null);
  const formRef = useRef<FormInstance | null>(null);
  const soloLectura = isManifoldCatalogReadOnly();
  const sucursalSesion = getSessionSucursalId();

  const [dataSource, setDataSource] = useState<LineaGridRow[]>([]);
  const [manifolds, setManifolds] = useState<ManifoldOption[]>([]);
  const [sucursales, setSucursales] = useState<SucursalOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<FormState | null>(null);
  const [isStatusPopupVisible, setIsStatusPopupVisible] = useState(false);
  const [itemToToggle, setItemToToggle] = useState<LineaGridRow | null>(null);

  const manifoldsFiltrados = editingItem?.id_Sucursal
    ? manifolds.filter(
        (m) =>
          m.id_Sucursal === Number(editingItem.id_Sucursal) &&
          isActivoEstatus(m.estatus),
      )
    : [];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [lineasRes, sucursalesRes, manifoldsRes] = await Promise.all([
        apiService.get<unknown[]>("/api/Catalogos/CatLinea"),
        apiService.get<Sucursal[]>("/api/Catalogos/Sucursales"),
        apiService.get<unknown[]>("/api/Catalogos/Registro_Manifold"),
      ]);

      const lineas = (lineasRes ?? [])
        .map(normalizeLinea)
        .filter((l) =>
          soloLectura
            ? sucursalSesion != null && l.id_Sucursal === sucursalSesion
            : true,
        );
      const sucursalesById = new Map<number, Sucursal>();
      const sucursalOptions: SucursalOption[] = (sucursalesRes ?? []).map(
        (s) => {
          const idNum = Number(s.id);
          sucursalesById.set(idNum, s);
          return {
            ...s,
            idNum,
            searchText: `${s.numSucursal ?? ""} ${s.nombreSuc ?? ""} ${s.id}`.trim(),
          };
        },
      );

      const manifoldOptions = (manifoldsRes ?? [])
        .map(normalizeManifoldOption)
        .filter((m): m is ManifoldOption => m != null)
        .map((m) => {
          const sucursal = sucursalesById.get(m.id_Sucursal);
          return {
            ...m,
            numSucursal: sucursal?.numSucursal ?? m.numSucursal,
            nombreSucursal: sucursal?.nombreSuc ?? m.nombreSucursal,
          };
        });
      const manifoldsById = new Map(
        manifoldOptions.map((m) => [m.id_Manifold, m]),
      );

      setSucursales(sucursalOptions);
      setManifolds(manifoldOptions);
      setDataSource(
        lineas.map((linea) => toGridRow(linea, sucursalesById, manifoldsById)),
      );
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [soloLectura, sucursalSesion]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();
    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Lineas");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "CatalogoLinea.xlsx",
    );
  };

  const handleAdd = () => {
    if (soloLectura) return;
    setEditingItem({
      nombreLinea: "",
      id_Manifold: null,
      id_Sucursal: null,
    });
    setIsFormPopupVisible(true);
  };

  const handleEdit = (row: LineaGridRow) => {
    if (soloLectura) return;
    setEditingItem({
      id_Linea: row.id_Linea,
      nombreLinea: row.nombreLinea,
      id_Manifold: row.id_Manifold,
      id_Sucursal: row.id_Sucursal,
    });
    setIsFormPopupVisible(true);
  };

  const handleToggleStatusRequest = (row: LineaGridRow) => {
    if (soloLectura) return;
    setItemToToggle(row);
    setIsStatusPopupVisible(true);
  };

  const confirmToggleStatus = async () => {
    if (!itemToToggle) return;
    try {
      await apiService.patch(
        `/api/Catalogos/CatLinea/${itemToToggle.id_Linea}/cambiastatus`,
        "",
      );
      notify(
        itemToToggle.activo
          ? "Línea desactivada con éxito."
          : "Línea activada con éxito.",
        "success",
        2000,
      );
      await fetchData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setIsStatusPopupVisible(false);
      setItemToToggle(null);
    }
  };

  const handleSave = async () => {
    if (soloLectura) return;
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify("Por favor, complete todos los campos requeridos.", "warning", 2000);
      return;
    }

    const formData = form.option("formData") as FormState;
    const idManifold = Number(formData.id_Manifold);
    if (!idManifold) {
      notify("El manifold es obligatorio.", "warning", 2000);
      return;
    }

    const manifold = manifolds.find((m) => m.id_Manifold === idManifold);
    if (!manifold) {
      notify("El manifold seleccionado no es válido.", "warning", 2000);
      return;
    }

    const idSucursalForm = Number(formData.id_Sucursal);
    if (!idSucursalForm) {
      notify("La sucursal es obligatoria.", "warning", 2000);
      return;
    }

    if (Number(manifold.id_Sucursal) !== idSucursalForm) {
      notify(
        "El manifold seleccionado no pertenece a la sucursal elegida.",
        "warning",
        3000,
      );
      return;
    }

    // Enviar nombres exactos que espera el DTO del API.
    const payload = {
      nombreLinea: formData.nombreLinea.trim(),
      id_Manifold: idManifold,
      id_Sucursal: Number(manifold.id_Sucursal),
    };

    try {
      if (formData.id_Linea) {
        await apiService.put<CatLinea, typeof payload>(
          `/api/Catalogos/CatLinea/${formData.id_Linea}`,
          payload,
        );
        notify("Línea actualizada con éxito.", "success", 2000);
      } else {
        await apiService.post<CatLinea, typeof payload>(
          "/api/Catalogos/CatLinea",
          payload,
        );
        notify("Línea creada con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
      await fetchData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 4000);
    }
  };

  const NombreCell = (cellData: { data: LineaGridRow }) => (
    <S.NameLink type="button" onClick={() => handleEdit(cellData.data)}>
      {cellData.data.nombreLinea}
    </S.NameLink>
  );

  const EstatusCell = (cellData: { data: LineaGridRow }) => (
    <S.EstatusBadge $active={cellData.data.activo}>
      {cellData.data.activo ? "Activo" : "Inactivo"}
    </S.EstatusBadge>
  );

  const ActionsCell = (cellData: { data: LineaGridRow }) => {
    const row = cellData.data;
    return (
      <div
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          hint="Editar"
          onClick={() => handleEdit(row)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={row.activo ? "Desactivar" : "Activar"}
          onClick={() => handleToggleStatusRequest(row)}
          stylingMode="text"
        >
          {row.activo ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <>
      <S.GlobalPopupStyles />
      <GridContainer title="Catálogo de Línea">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "16px 24px 12px",
            borderBottom: "1px solid #DFE1E6",
          }}
        >
          <Button
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            onClick={() => navigate(-1)}
          />
        </div>

        <DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          keyExpr="id_Linea"
          showBorders
          columnAutoWidth
          wordWrapEnabled
          hoverStateEnabled
          loadPanel={{ enabled: loading }}
        >
          <Paging defaultPageSize={10} />
          <FilterRow visible />
          <HeaderFilter visible allowSearch />
          <SearchPanel visible highlightCaseSensitive placeholder="Buscar..." />
          <Toolbar>
            <Item name="searchPanel" />
            <Item
              location="after"
              widget="dxButton"
              options={{
                text: "Exportar Excel",
                icon: "xlsxfile",
                stylingMode: "contained",
                type: "default",
                onClick: handleExportExcel,
              }}
            />
            {!soloLectura && (
            <Item
              location="after"
              widget="dxButton"
              options={{
                text: "Añadir Línea",
                icon: "add",
                stylingMode: "contained",
                type: "default",
                onClick: handleAdd,
              }}
            />
            )}
          </Toolbar>

          <Column
            dataField="nombreLinea"
            caption="Nombre Línea"
            cellRender={soloLectura ? undefined : NombreCell}
            minWidth={160}
          />
          <Column
            dataField="numCodSucursal"
            caption="ID Sucursal"
            width={120}
            alignment="center"
          />
          <Column
            dataField="nombreSucursal"
            caption="Nombre Sucursal"
            minWidth={140}
          />
          <Column
            dataField="nombreManifold"
            caption="Manifold"
            minWidth={160}
          />
          <Column
            dataField="estatus"
            caption="Estatus"
            width={110}
            alignment="center"
            cellRender={EstatusCell}
          />
          {!soloLectura && (
          <Column
            caption="Acciones"
            width={130}
            alignment="center"
            cellRender={ActionsCell}
            allowExporting={false}
          />
          )}
        </DataGrid>
      </GridContainer>

      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton
        showTitle
        title={
          editingItem?.id_Linea
            ? `Editar Línea: ${editingItem.nombreLinea}`
            : "Añadir Línea"
        }
        width={560}
        height="auto"
        className="custom-form-popup"
      >
        <S.FormContainer>
          <Form
            key={`linea-form-${editingItem?.id_Sucursal ?? "new"}-${editingItem?.id_Linea ?? "add"}`}
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
            onFieldDataChanged={(e) => {
              if (e.dataField !== "id_Sucursal") return;

              const idSucursal = Number(e.value) || null;
              const current =
                (formRef.current?.option("formData") as FormState) ??
                editingItem;
              const nombreLinea = current?.nombreLinea ?? "";
              const currentManifold = current?.id_Manifold
                ? Number(current.id_Manifold)
                : null;
              const manifoldOk =
                currentManifold != null &&
                manifolds.some(
                  (m) =>
                    m.id_Manifold === currentManifold &&
                    m.id_Sucursal === Number(idSucursal),
                );

              setEditingItem({
                id_Linea: current?.id_Linea,
                nombreLinea,
                id_Sucursal: idSucursal,
                id_Manifold: manifoldOk ? currentManifold : null,
              });
            }}
          >
            <SimpleItem
              dataField="nombreLinea"
              editorOptions={{
                placeholder: "Ej. Línea 3",
              }}
            >
              <Label text="Nombre Línea" />
              <RequiredRule message="El nombre es requerido." />
            </SimpleItem>
            <SimpleItem
              dataField="id_Sucursal"
              editorType="dxSelectBox"
              editorOptions={{
                items: sucursales,
                displayExpr: (item: SucursalOption) =>
                  item
                    ? `${item.numSucursal ?? ""} — ${item.nombreSuc ?? ""}`
                    : "",
                valueExpr: "idNum",
                searchEnabled: true,
                searchExpr: ["searchText", "numSucursal", "nombreSuc"],
                placeholder: "Buscar por ID o nombre de sucursal",
                showClearButton: true,
              }}
            >
              <Label text="Sucursal" />
              <RequiredRule message="La sucursal es requerida." />
            </SimpleItem>
            <SimpleItem
              dataField="id_Manifold"
              editorType="dxSelectBox"
              editorOptions={{
                items: manifoldsFiltrados,
                displayExpr: "nombre",
                valueExpr: "id_Manifold",
                searchEnabled: true,
                disabled: !editingItem?.id_Sucursal,
                placeholder: editingItem?.id_Sucursal
                  ? "Buscar manifold de la sucursal seleccionada"
                  : "Seleccione primero una sucursal",
                noDataText: editingItem?.id_Sucursal
                  ? "No hay manifolds para esta sucursal"
                  : "Seleccione una sucursal",
                showClearButton: true,
              }}
            >
              <Label text="Manifold" />
              <RequiredRule message="El manifold es requerido." />
            </SimpleItem>
          </Form>
        </S.FormContainer>
        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsFormPopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      <Popup
        visible={isStatusPopupVisible}
        onHiding={() => {
          setIsStatusPopupVisible(false);
          setItemToToggle(null);
        }}
        dragEnabled={false}
        showTitle
        title={`Confirmar ${itemToToggle?.activo ? "desactivación" : "activación"}`}
        width={420}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${itemToToggle?.activo ? "desactivar" : "activar"}`,
              type: itemToToggle?.activo ? "danger" : "success",
              stylingMode: "contained",
              onClick: confirmToggleStatus,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => {
                setIsStatusPopupVisible(false);
                setItemToToggle(null);
              },
            },
          },
        ]}
      >
        <div style={{ padding: 20, textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>
              {itemToToggle?.activo ? "desactivar" : "activar"}
            </strong>{" "}
            la línea
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToToggle?.nombreLinea}"?
          </p>
        </div>
      </Popup>
    </>
  );
};

export default LineaIndex;
